import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
  getAllSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
} from "../../../services/suppliersService";
import { Supplier, SupplierCreatePayload, SupplierUpdatePayload, PaginatedSuppliers } from "../../../types/suppliersTypes";

export const useSuppliers = () => {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState<PaginatedSuppliers>({
    data: [],
    pagination: {
      total: 0,
      page: 1,
      limit: 10000
    }
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const fetchSuppliers = useCallback(async (filters: { [key: string]: string | number | null } = {}) => {
    setLoading(true);
    try {
      const response = await getAllSuppliers(filters);
      setSuppliers(response.data);
      setError(null);
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_fetch_suppliers"));
      setError(t("failed_to_fetch_suppliers"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleCreate = async (payload: SupplierCreatePayload) => {
    try {
      await createSupplier(payload);
      message.success(t("supplier_added_successfully"));
      await fetchSuppliers();
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_create_supplier"));
      return false;
    }
  };

  const handleUpdate = async (supplierId: string, payload: SupplierUpdatePayload) => {
    try {
      await updateSupplier(supplierId, payload);
      message.success(t("supplier_updated_successfully"));
      await fetchSuppliers();
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_update_supplier"));
      return false;
    }
  };

  const handleDelete = async (supplierId: string) => {
    try {
      await deleteSupplier(supplierId);
      message.success(t("supplier_deleted_successfully"));
      await fetchSuppliers();
      return true;
    } catch (err) {
      console.error(err);
      // message.error(t("failed_to_delete_supplier"));
      return false;
    }
  };

  return {
    suppliers: suppliers.data,
    infoPagination: {
      total: suppliers.pagination.total,
      page: suppliers.pagination.page,
      limit: suppliers.pagination.limit,
    },
    loading,
    error,
    selectedSupplier,
    setSelectedSupplier,
    isModalOpen,
    setIsModalOpen,
    fetchSuppliers,
    handleCreate,
    handleUpdate,
    handleDelete
  };
};

export default useSuppliers;
